<template>
  <div
    v-if="!loading && (recommendations.length || isControlGroup)"
    :class="['mx-5', { _trbo_control: isControlGroup }]"
    :data-test-id="baseId"
    :data-id="baseId"
  >
    <hr v-if="isAddToBasketPopover" class="w-full border-t border-black-10" />
    <!-- hide the headline only when goControl is active and TRBO consent is rejected -->
    <div
      v-if="title && (!isControlGroup || hasConsent)"
      :class="{
        'my-2': isAddToBasketPopover,
        'my-10': !isAddToBasketPopover && !showReducedRecosSpacing,
        'mb-5 mt-10': !isAddToBasketPopover && showReducedRecosSpacing,
      }"
      :data-test-id="`${baseId}_title`"
    >
      <span
        :class="{
          'font-semibold text-lg': isAddToBasketPopover,
          'text-xl font-normal': isPdp && !isAddToBasketPopover,
          'text-xl font-bold': !isPdp && !isAddToBasketPopover,
        }"
      >
        {{ title }}
      </span>
    </div>
    <BasicSlider
      v-if="!isControlGroup"
      :items="recommendations"
      key-prop="id"
      :pixel-gap="0"
      :center-content="recommendations.length < 4 && props.centerContent"
      :data-test-id="`${baseId}_slider`"
      @slide-visible="onSlideVisible"
    >
      <template #item="{ item: product, index }">
        <ProductCard
          :id="product.id"
          :key="`product-recommendation-${product.id}`"
          :product="product"
          :siblings="getSiblingsColors(product) as ProductSibling[]"
          :colors="getProductAndSiblingsColors(product, 'colorRange')"
          :name="getName(product)"
          :link="toLocalePath(getProductDetailRoute(product))"
          :image="getProductImage(product.images, imageAssetType)"
          :disable-hover-image="true"
          :price="lowestPrice(product)"
          :show-add-to-cart="false"
          :show-add-to-wishlist="false"
          :show-available-colors="false"
          :show-price-reduction="false"
          :hide-more-colors-label="true"
          highlight-price-red
          class="px-1"
          @click:product="handleProductClick(product, index)"
        />
      </template>
    </BasicSlider>
  </div>
</template>

<script setup lang="ts">
import type { Product, ProductSibling } from '@scayle/storefront-nuxt'
import {
  getFirstAttributeValue,
  getProductAndSiblingsColors,
} from '@scayle/storefront-nuxt'
import {
  OdoscopeRecommendationsToken,
  RECO_LIST_CONTEXT,
} from '#layers/base/constants/recommendations'
import useOdoscopeRecommendations from '~/composables/useOdoscopeRecommendations'
import useCarouselListTracking from '#layers/base/composables/useCarouselListTracking'
import BasicSlider from '~/components/ui/basicSlider/BasicSlider.vue'
import useUiState from '~/composables/ui/useUiState'
import useExperiments from '~/composables/useExperiments'
import { useServiceConsent } from '~/composables/useServiceConsent'

const props = withDefaults(
  defineProps<{
    token: OdoscopeRecommendationsToken
    title: string
    categoryId?: number
    productId?: number
    isAddToBasketPopover?: boolean
    centerContent?: boolean
    fetchOnClient?: boolean
  }>(),
  {
    categoryId: undefined,
    centerContent: true,
    fetchOnClient: false,
    productId: undefined,
  },
)

const emit = defineEmits(['click:recommendation'])

const { isPdp } = useUiState()
const { toLocalePath } = useRouteHelpers()
const { hasConsent } = useServiceConsent('trbo')
const { showReducedRecosSpacing } = useExperiments()

const { recommendations, fetchRecommendations, loading, goControl } =
  useOdoscopeRecommendations(props.token, props.categoryId, props.productId)

const { onSlideVisible, onSelect } = useCarouselListTracking({
  context: computed(() => RECO_LIST_CONTEXT[props.token]),
  total: () => recommendations.value.length,
  // Never track Odoscope control-group lists, for any token (the rendering-side
  // isControlGroup only covers similar_products).
  shouldTrack: () => !goControl.value,
})

const handleProductClick = (product: Product, index: number) => {
  onSelect(product, index)
  emit('click:recommendation', product, index)
}

const imageAssetType = '_f'
const baseId = computed(() => {
  return `recommendations_${props.token.toLowerCase()}`
})

const isControlGroup = computed(
  () =>
    props.token === OdoscopeRecommendationsToken.SIMILAR_PRODUCTS &&
    goControl.value,
)

if (!props.fetchOnClient) {
  onServerPrefetch(() => fetchRecommendations())
}

onMounted(() => {
  void fetchRecommendations()
})

const getName = (product: Product) =>
  getFirstAttributeValue(product.attributes, 'name')?.label || ''

const lowestPrice = (product: Product) => getLowestPrice(product.variants || [])
</script>
